import { useState } from 'react';
import { buyGiftCard } from '@/shared/services/cashService';
import { useToast } from '@/shared/contexts/ToastContext';
import { PanelTitle } from './PanelTitle';

// Gift cards vendidos no dashboard (direto por Cash).
// Backend: POST /Cash/giftcard?kind=1|2|3 — executa up_AddMyCashItemByItem.
const GIFT_CARDS = {
  1: { name: 'Cartão de Presente (Bronze)', shortName: 'Bronze', price: 200, css: 'bronze', image: '/assets/bronze2.webp' },
  2: { name: 'Cartão de Presente (Prata)', shortName: 'Prata', price: 500, css: 'silver', image: '/assets/prata2.webp' },
  3: { name: 'Cartão de Presente (Ouro)', shortName: 'Ouro', price: 1000, css: 'goldgift', image: '/assets/ouro2.webp' },
} as const;

type GiftKind = keyof typeof GIFT_CARDS;

// Reembolso de cash ainda nao consumido (CabalCash._39_CashPending): enquanto
// sobrar, nao compra cartao -- o reembolso tem que ser GASTO no jogo. O servidor
// tambem barra (CASH_PENDING); aqui e' so' pra avisar antes.
const pendingMsg = (p: number) =>
  `Você precisa consumir ${p.toLocaleString('pt-BR')} LCoin antes de comprar cartão cash.`;

export function GiftsPanel({ cash, pending = 0, onBought }: { cash: number; pending?: number; onBought: () => Promise<void> }) {
  const { notify } = useToast();
  const [buying, setBuying] = useState<GiftKind | null>(null);
  const [modal, setModal] = useState<GiftKind | null>(null);

  const openBuy = (kind: GiftKind) => {
    if (pending > 0) { notify('error', pendingMsg(pending)); return; }
    setModal(kind);
  };

  // Pre-check de saldo aqui pra evitar request a toa quando falta cash.
  const buy = async (kind: GiftKind, quantity: number) => {
    const card = GIFT_CARDS[kind];
    const total = card.price * quantity;
    if (cash < total) {
      notify('error', `lcoin insuficiente. Voce tem ${cash.toLocaleString('pt-BR')}, precisa de ${total.toLocaleString('pt-BR')}.`);
      return;
    }
    setBuying(kind);
    setModal(null);
    try {
      const res = await buyGiftCard(kind, quantity);
      notify('success', res.message || `${card.name}${quantity > 1 ? ` x${quantity}` : ''} adicionado!`);
      await onBought();
    } catch (err: any) {
      const msg = err?.message || err?.detail?.Message || 'Erro ao comprar cartão';
      notify('error', typeof msg === 'string' ? msg : 'Erro ao comprar cartão');
    } finally {
      setBuying(null);
    }
  };

  return (
    <section className="panel-surface gifts-panel">
      <PanelTitle icon="♜" title="Cartões de Presente" subtitle="Compre direto com lcoin — entrega instantânea no inventário" />
      {pending > 0 && (
        <div className="mb-3 rounded-md border border-amber-400/40 bg-amber-400/10 px-4 py-3 text-sm text-amber-100">
          <strong className="text-amber-300">Atenção:</strong> você ainda precisa consumir{' '}
          <strong className="text-amber-300">{pending.toLocaleString('pt-BR')} LCoin</strong> no jogo antes de poder comprar cartão cash.
        </div>
      )}
      <div className="gift-grid">
        {([1, 2, 3] as const).map((kind) => {
          const card = GIFT_CARDS[kind];
          const canBuy = cash >= card.price;
          return (
            <article key={kind} className={`gift-card has-art ${card.css}`}>
              {/* desenho do cartao (o mesmo do painel antigo) */}
              <img className="gift-art" src={card.image} alt={card.name} draggable={false} />
              <div>
                <small>{card.shortName}</small>
                <strong>{card.price.toLocaleString('pt-BR')} <em>lcoin</em></strong>
                <button disabled={buying === kind || (!canBuy && pending <= 0)} onClick={() => openBuy(kind)}>
                  {buying === kind ? 'Comprando...' : canBuy ? 'Comprar' : 'lcoin insuficiente'}
                </button>
              </div>
            </article>
          );
        })}
      </div>

      {modal != null && (
        <GiftQuantityModal
          card={GIFT_CARDS[modal]}
          currentCash={cash}
          isBuying={buying === modal}
          onClose={() => setModal(null)}
          onConfirm={(qty) => buy(modal, qty)}
        />
      )}
    </section>
  );
}

function GiftQuantityModal({ card, currentCash, isBuying, onClose, onConfirm }: {
  card: (typeof GIFT_CARDS)[GiftKind];
  currentCash: number;
  isBuying: boolean;
  onClose: () => void;
  onConfirm: (qty: number) => void;
}) {
  const [qty, setQty] = useState(1);
  const total = card.price * qty;
  const insufficient = currentCash < total;
  const max = Math.min(99, Math.floor(currentCash / card.price));

  return (
    <div className="lb-modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="lb-modal ornate-card" onClick={(e) => e.stopPropagation()}>
        <h3>{card.name}</h3>
        <p>{card.price.toLocaleString('pt-BR')} lcoin por unidade</p>
        <div className="qty-row">
          <button onClick={() => setQty((q) => Math.max(1, q - 1))} disabled={qty <= 1} aria-label="Diminuir">−</button>
          <input
            type="number"
            inputMode="numeric"
            min={1}
            max={99}
            value={qty}
            onChange={(e) => {
              const n = parseInt(e.target.value, 10);
              setQty(isNaN(n) ? 1 : Math.max(1, Math.min(99, n)));
            }}
          />
          <button onClick={() => setQty((q) => Math.min(99, q + 1))} disabled={qty >= 99} aria-label="Aumentar">+</button>
        </div>
        {max > 0 && qty < max && (
          <button className="qty-max" onClick={() => setQty(max)}>Comprar o máximo possível ({max})</button>
        )}
        <div className="qty-total">
          <small>Total</small>
          <div>
            <strong>{total.toLocaleString('pt-BR')}</strong> <small className="lcoin">lcoin</small>
            {insufficient && <em>Saldo insuficiente · você tem {currentCash.toLocaleString('pt-BR')}</em>}
          </div>
        </div>
        <div className="lb-modal-actions">
          <button className="btn btn-dark" onClick={onClose}>Cancelar</button>
          <button className="btn btn-gold" onClick={() => onConfirm(qty)} disabled={isBuying || insufficient}>
            {isBuying ? 'Comprando...' : 'Comprar agora'}
          </button>
        </div>
      </div>
    </div>
  );
}
